/**
 * Pure operations on a `list` field's value (DAB-852).
 *
 * A list value is `{ items: [{ id, hidden?, ...parts }] }`, or `null` for
 * "untouched — the site's own list". The Bulgarian document owns the
 * structure (order, `hidden`, which ids exist); the English one stores text per
 * id only. `field.fallback` is the committed `{ items }` for the language being
 * edited — `localiseManifest` has already resolved it.
 *
 * Generalises `Testimonials/testimonialOrder.js`: moves are neighbour swaps and
 * an impossible move returns the value unchanged (same reference), so a caller
 * can tell a no-op without comparing.
 *
 * The rule carried over from every other field: anything that merely equals
 * what the site already shows is sent as cleared, so opening a list and
 * leaving it never takes ownership of it away from hpn-landing.
 */

/** Matches `LIST_ITEM_ID`'s generated shape in my-pet-api's page controller. */
export const newItemId = (random = Math.random) =>
  `n-${Array.from({ length: 8 }, () => Math.floor(random() * 16).toString(16)).join("")}`;

/** A professional's user id — the only id a picker list may hold. Mirrors `PROFESSIONAL_ID` in my-pet-api. */
const PROFESSIONAL_ID = /^[a-f0-9]{24}$/i;

/** The API's ceiling when a manifest declares no `max`. */
const LIST_MAX_ITEMS = 50;

export const isListValue = (value) =>
  !!value && typeof value === "object" && !Array.isArray(value) && Array.isArray(value.items);

const committedItems = (field) => (isListValue(field.fallback) ? field.fallback.items : []);

const committedById = (field) => new Map(committedItems(field).map((item) => [item.id, item]));

/**
 * A picker list (the featured professionals) is the editor's whole selection:
 * its committed items are only where it started. Every other list's committed
 * items ship with the code, so the site appends any a stored value predates —
 * and this file mirrors that, or the editor would reorder a different list
 * from the one the page renders. Same rule as `appendMissing` in hpn-landing's
 * `lib/cmsMerge.ts`.
 */
const isSelection = (field) => field.picker === "professional";

/** Parts an editor may change — locked ones are code-owned and never sent. */
export const editableParts = (field) => (field.itemFields || []).filter((part) => !part.locked);

const isEmptyPart = (value) =>
  value === null ||
  value === undefined ||
  value === "" ||
  (typeof value === "object" && !Array.isArray(value) && Object.values(value).every(isEmptyPart));

/** A part equals the committed one when empty (= use committed) or identical, part by part. */
const partEquals = (value, committed) => {
  if (isEmptyPart(value)) return true;
  if (typeof value === "string") return value === committed;
  if (typeof value === "object") {
    const base = committed && typeof committed === "object" ? committed : {};
    return Object.keys({ ...base, ...value }).every(
      (key) => isEmptyPart(value[key]) || value[key] === base[key],
    );
  }
  return false;
};

/**
 * The value as a full Bulgarian structure: a copy of the stored items, with any
 * committed id they do not mention appended — the same rule the site renders
 * by, so what the editor reorders is what the page shows. (Not for a picker
 * list, see `isSelection`.)
 */
const materialise = (field, value) => {
  const committed = committedItems(field);
  if (!isListValue(value)) return { items: committed.map(({ id }) => ({ id })) };
  const items = value.items.map((item) => ({ ...item }));
  if (isSelection(field)) return { items };
  const present = new Set(items.map((item) => item.id));
  committed.forEach(({ id }) => {
    if (!present.has(id)) items.push({ id });
  });
  return { items };
};

export const moveItem = (field, value, id, direction) => {
  const next = materialise(field, value);
  const i = next.items.findIndex((item) => item.id === id);
  const j = direction === "up" ? i - 1 : i + 1;
  if (i < 0 || j < 0 || j >= next.items.length) return value;
  [next.items[i], next.items[j]] = [next.items[j], next.items[i]];
  return next;
};

/** Showing is always allowed; hiding must leave at least `min` (and never 0) visible. */
export const canHideItem = (field, value, id) => {
  const { items } = materialise(field, value);
  const item = items.find((candidate) => candidate.id === id);
  if (!item) return false;
  if (item.hidden) return true;
  const visible = items.filter((candidate) => !candidate.hidden).length;
  return visible - 1 >= Math.max(1, field.min || 0);
};

export const toggleHidden = (field, value, id) => {
  if (!canHideItem(field, value, id)) return value;
  const next = materialise(field, value);
  const item = next.items.find((candidate) => candidate.id === id);
  if (item.hidden) delete item.hidden;
  else item.hidden = true;
  return next;
};

export const canAddItem = (field, value) =>
  Boolean(field.addable) && materialise(field, value).items.length < (field.max || LIST_MAX_ITEMS);

export const addItem = (field, value, id) => {
  if (!canAddItem(field, value)) return value;
  const next = materialise(field, value);
  next.items.push({ id });
  return next;
};

/**
 * Only items the site does not ship can be removed; a committed one can only
 * be hidden. A picker list's items are all the editor's to remove.
 */
export const isRemovable = (field, id) => isSelection(field) || !committedById(field).has(id);

export const removeItem = (field, value, id) => {
  if (!isRemovable(field, id)) return value;
  const next = materialise(field, value);
  const index = next.items.findIndex((item) => item.id === id);
  if (index < 0) return value;
  next.items.splice(index, 1);
  return next;
};

/**
 * Set one part of one item. On Bulgarian that edits the structure; on any
 * other language it records text against the id and nothing else.
 */
export const setItemPart = (field, value, id, key, part, lang) => {
  const next =
    lang === "bg"
      ? materialise(field, value)
      : { items: isListValue(value) ? value.items.map((item) => ({ ...item })) : [] };
  let item = next.items.find((candidate) => candidate.id === id);
  if (!item) {
    item = { id };
    next.items.push(item);
  }
  item[key] = part;
  return next;
};

/**
 * Ids a picker list holds that are not professional ids — only possible from
 * the pre-DAB-852 comma-separated text. The API refuses the whole save over
 * one of them, so they are shown as a warning and left out of the payload.
 */
export const invalidItemIds = (field, value) =>
  isSelection(field) && isListValue(value)
    ? value.items.map((item) => String(item.id)).filter((id) => !PROFESSIONAL_ID.test(id))
    : [];

const withoutInvalidIds = (field, value) => {
  const invalid = invalidItemIds(field, value);
  return invalid.length
    ? { items: value.items.filter((item) => !invalid.includes(String(item.id))) }
    : value;
};

/** Does this value render exactly what the site already shows? */
export const listEqualsFallback = (field, value, lang) => {
  if (!isListValue(value) || value.items.length === 0) return true;
  const committed = committedItems(field);
  const byId = committedById(field);
  const parts = editableParts(field);
  const untouched = (item) => {
    const base = byId.get(item.id);
    return parts.every((part) =>
      base ? partEquals(item[part.key], base[part.key]) : isEmptyPart(item[part.key]),
    );
  };

  if (lang !== "bg") return value.items.every(untouched);

  const { items } = materialise(field, value);
  return (
    items.length === committed.length &&
    items.every((item, index) => item.id === committed[index].id && !item.hidden && untouched(item))
  );
};

/**
 * The save payload for one list: `null` when it equals the site, otherwise the
 * items with only the parts that differ from what is committed. English drops
 * `hidden` and any item left without text of its own, and any id the displayed
 * Bulgarian `structure` (see `displayItems`) does not hold — stored English
 * the tab never shows, which the API cannot accept for a non-addable list.
 */
export const listPayload = (field, rawValue, lang, structure) => {
  const value = withoutInvalidIds(field, rawValue);
  if (lang !== "bg" && isListValue(value)) {
    const shown = new Set(
      materialise(field, isListValue(structure) ? structure : null).items.map((item) => item.id),
    );
    return listPayloadFor(field, { items: value.items.filter((item) => shown.has(item.id)) }, lang);
  }
  return listPayloadFor(field, value, lang);
};

const listPayloadFor = (field, value, lang) => {
  if (listEqualsFallback(field, value, lang)) return null;
  const byId = committedById(field);
  const parts = editableParts(field);
  const source = lang === "bg" ? materialise(field, value).items : value.items;

  const items = source.map((item) => {
    const out = { id: item.id };
    if (lang === "bg" && item.hidden) out.hidden = true;
    const base = byId.get(item.id);
    parts.forEach(({ key }) => {
      if (isEmptyPart(item[key])) return;
      if (base && partEquals(item[key], base[key])) return;
      out[key] = item[key];
    });
    return out;
  });

  if (lang === "bg") return { items };
  const withText = items.filter((item) => Object.keys(item).length > 1);
  return withText.length ? { items: withText } : null;
};

/**
 * What the editor sees, one row per item, in render order.
 *
 * English takes order and `hidden` from `structure` — the Bulgarian draft of
 * the same list — and text from its own value. A Bulgarian item with no
 * English yet shows `missing` parts, which is how the card says "untranslated".
 */
export const displayItems = (field, value, lang, structure) => {
  const byId = committedById(field);
  const parts = editableParts(field);

  let order;
  let textFor;
  if (lang === "bg") {
    order = materialise(field, value).items;
    textFor = (entry) => entry;
  } else {
    order = materialise(field, isListValue(structure) ? structure : null).items;
    const own = new Map((isListValue(value) ? value.items : []).map((item) => [item.id, item]));
    textFor = (entry) => own.get(entry.id) || {};
  }

  return order.map((entry) => {
    const source = textFor(entry);
    const itemParts = {};
    parts.forEach(({ key }) => {
      if (source[key] !== undefined) itemParts[key] = source[key];
    });
    const base = byId.get(entry.id) || null;
    return {
      id: entry.id,
      hidden: Boolean(entry.hidden),
      isNew: !base,
      committed: base,
      parts: itemParts,
      missing: base
        ? []
        : parts.filter(({ key, required }) => required && isEmptyPart(itemParts[key])).map(({ key }) => key),
    };
  });
};

/** A card's one-line title: the first text part, edited or committed, else the id. */
export const itemSummary = (field, row, limit = 80) => {
  const textPart = (field.itemFields || []).find(
    (part) => part.kind === "text" || part.kind === "paragraph",
  );
  const own = textPart ? row.parts[textPart.key] : "";
  const text = String((!isEmptyPart(own) && own) || (textPart && row.committed?.[textPart.key]) || "").trim();
  if (!text) return row.id;
  return text.length > limit ? `${text.slice(0, limit).trimEnd()}…` : text;
};

/** Every string part of every item, for the editor's search. */
export const listSearchText = (value) =>
  isListValue(value)
    ? value.items
        .flatMap((item) =>
          Object.entries(item)
            .filter(([key]) => key !== "id" && key !== "hidden")
            .flatMap(([, part]) =>
              typeof part === "string"
                ? [part]
                : part && typeof part === "object"
                  ? Object.values(part).filter((inner) => typeof inner === "string")
                  : [],
            ),
        )
        .join(" ")
    : "";

/** Canonical form for comparing two stored list values (publish delta). */
const canonicalList = (value) =>
  isListValue(value) && value.items.length
    ? value.items.map((item) =>
        Object.keys(item)
          .filter((key) => !(key === "hidden" && !item.hidden))
          .sort()
          .reduce((acc, key) => {
            acc[key] = item[key];
            return acc;
          }, {}),
      )
    : null;

export const sameListValue = (a, b) =>
  JSON.stringify(canonicalList(a)) === JSON.stringify(canonicalList(b));
